import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    DocumentTextIcon,
    EyeIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { fetchTimeline, invalidateTimelineEvent } from '../../../../services/timeline';
import { fetchDxReportDrafts, deleteDxReportDraft } from '../../../../services/clinicalDrafts';
import { fetchCloudFileDownloadUrl } from '../../../../services/patientFiles';
import { useUser } from '../../../../providers/UserProvider';
import { useInvalidateDxReportDrafts } from '../../../../hooks/useDxReportDrafts';
import { DxReportEditor } from './DxReportEditor';
import type { ActionRunnerProps, TimelineEvent } from '../types';
import {
    buildFileName,
    formatRelativeTime,
    getDraftReviewInfo,
    getDxPatientFileId
} from './landing/utils';
import { DraftStatusBadge } from './landing/DraftStatusBadge';
import { NewDxReportDraftModal } from './landing/NewDxReportDraftModal';
import { NewVersionFromPreviousModal } from './landing/NewVersionFromPreviousModal';
import { DeleteDraftConfirmModal } from './landing/DeleteDraftConfirmModal';
import { DeleteSubmittedConfirmModal } from './landing/DeleteSubmittedConfirmModal';
import { DxReportPreviewPane } from './landing/DxReportPreviewPane';

type View =
    | { kind: 'list' }
    | { kind: 'create'; resumingDraft?: TimelineEvent }
    | { kind: 'edit'; event: TimelineEvent; resumingDraft?: TimelineEvent; startBlank?: boolean }
    | { kind: 'preview'; url: string; fileName: string; version: number };

interface DxReportLandingPageProps extends ActionRunnerProps {
    mode?: 'author';
    onRegisterBeforeClose?: (handler: (() => Promise<void>) | null) => void;
}

function DxReportLandingPage({
    patientId,
    patientName,
    orthopulseStatus,
    onBack,
    onDataChange,
    mode,
    onRegisterBeforeClose
}: DxReportLandingPageProps) {
    const { user } = useUser();
    const isAdmin = user?.database.data.isAdmin ?? false;
    const invalidateDrafts = useInvalidateDxReportDrafts(patientId);

    const [view, setView] = useState<View>({ kind: 'list' });
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [drafts, setDrafts] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [previewingId, setPreviewingId] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<TimelineEvent | null>(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
    const [draftActionError, setDraftActionError] = useState<string | null>(null);
    const [newDxDraftPrompt, setNewDxDraftPrompt] = useState<TimelineEvent | null>(null);
    const [newVersionPrompt, setNewVersionPrompt] = useState<TimelineEvent | null>(null);
    const [pendingDraftDelete, setPendingDraftDelete] = useState<TimelineEvent | null>(null);

    const loadEvents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [eventsResult, draftsResult] = await Promise.all([
                // Omit `isLatest` so the list shows the full version history
                // (v1, v2, v3...) - not just the head row of each group. Past
                // versions stay `status='active'` with `isLatest=false`; the
                // sortedEvents memo orders everything newest-first by createdAt.
                fetchTimeline(patientId, {
                    type: 'dx_report_submission',
                    status: ['active', 'invalidated'],
                    limit: 50
                }),
                fetchDxReportDrafts(patientId)
            ]);
            setEvents(eventsResult.data);
            setDrafts(draftsResult);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load Dx reports';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    const sortedEvents = useMemo(
        () =>
            [...events].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            ),
        [events]
    );

    const handleEditorBack = useCallback(() => {
        setView({ kind: 'list' });
        loadEvents();
        onDataChange?.();
    }, [loadEvents, onDataChange]);

    const handleNewDxReport = useCallback(() => {
        // Order matters:
        //   1. Submitted versions exist  -> ask "Start over / Start from previous"
        //   2. Stranded draft (no parent) -> ask "Resume / Start fresh"
        //   3. Empty                      -> open the editor directly
        const latestSubmitted = sortedEvents.find((e) => e.status === 'active');
        if (latestSubmitted) {
            setNewVersionPrompt(latestSubmitted);
            return;
        }
        const existingNewDraft = drafts.find((d) => !events.some((e) => e.groupId === d.groupId));
        if (existingNewDraft) {
            setNewDxDraftPrompt(existingNewDraft);
        } else {
            setView({ kind: 'create' });
        }
    }, [drafts, events, sortedEvents]);

    const handleStartOverFromPrevious = useCallback(() => {
        const event = newVersionPrompt;
        setNewVersionPrompt(null);
        if (!event) return;
        setView({ kind: 'edit', event, startBlank: true });
    }, [newVersionPrompt]);

    const handleFromPrevious = useCallback(() => {
        const event = newVersionPrompt;
        setNewVersionPrompt(null);
        if (!event) return;
        setView({ kind: 'edit', event });
    }, [newVersionPrompt]);

    const handleResumeNewDraft = useCallback(() => {
        if (!newDxDraftPrompt) return;
        setNewDxDraftPrompt(null);
        setView({ kind: 'create', resumingDraft: newDxDraftPrompt });
    }, [newDxDraftPrompt]);

    const handleStartFresh = useCallback(async () => {
        const draft = newDxDraftPrompt;
        setNewDxDraftPrompt(null);
        if (draft) {
            try {
                await deleteDxReportDraft(patientId, draft.id);
                setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
                invalidateDrafts();
                onDataChange?.();
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to delete draft';
                setDraftActionError(message);
            }
        }
        setView({ kind: 'create' });
    }, [newDxDraftPrompt, patientId, invalidateDrafts, onDataChange]);

    const handleDeleteDraft = useCallback(
        async (draft: TimelineEvent) => {
            setDeletingDraftId(draft.id);
            setDraftActionError(null);
            try {
                await deleteDxReportDraft(patientId, draft.id);
                setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
                invalidateDrafts();
                onDataChange?.();
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to delete draft';
                setDraftActionError(message);
            } finally {
                setDeletingDraftId(null);
            }
        },
        [patientId, invalidateDrafts, onDataChange]
    );

    const handleConfirmDraftDelete = useCallback(async () => {
        if (!pendingDraftDelete) return;
        const draft = pendingDraftDelete;
        setPendingDraftDelete(null);
        await handleDeleteDraft(draft);
    }, [pendingDraftDelete, handleDeleteDraft]);

    const handlePreview = useCallback(
        async (event: TimelineEvent) => {
            const fileId = getDxPatientFileId(event);
            if (!fileId) {
                setPreviewError('No PDF available for this Dx report.');
                return;
            }
            setPreviewingId(event.id);
            setPreviewError(null);
            try {
                const url = await fetchCloudFileDownloadUrl(patientId, fileId);
                setView({
                    kind: 'preview',
                    url,
                    fileName: buildFileName(event),
                    version: event.version
                });
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to load preview';
                setPreviewError(message);
            } finally {
                setPreviewingId(null);
            }
        },
        [patientId]
    );

    const handleConfirmDelete = useCallback(async () => {
        if (!confirmDelete) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await invalidateTimelineEvent(patientId, confirmDelete.id, deleteReason || null);
            setConfirmDelete(null);
            setDeleteReason('');
            await loadEvents();
            onDataChange?.();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete Dx report';
            setDeleteError(message);
        } finally {
            setIsDeleting(false);
        }
    }, [confirmDelete, deleteReason, patientId, loadEvents, onDataChange]);

    const handleCancelDelete = useCallback(() => {
        setConfirmDelete(null);
        setDeleteReason('');
        setDeleteError(null);
    }, []);

    if (view.kind === 'create') {
        return (
            <DxReportEditor
                patientId={patientId}
                patientName={patientName}
                onBack={handleEditorBack}
                resumingDraft={view.resumingDraft}
                onRegisterBeforeClose={onRegisterBeforeClose}
            />
        );
    }

    if (view.kind === 'edit') {
        return (
            <DxReportEditor
                patientId={patientId}
                patientName={patientName}
                onBack={handleEditorBack}
                editingEvent={view.event}
                resumingDraft={view.resumingDraft}
                startBlank={view.startBlank}
                onRegisterBeforeClose={onRegisterBeforeClose}
            />
        );
    }

    // orthopulseStatus is unused in Dx report landing page but is part of the
    // ActionRunnerProps contract; reference it to silence lint.
    void orthopulseStatus;

    if (view.kind === 'preview') {
        return (
            <DxReportPreviewPane
                url={view.url}
                fileName={view.fileName}
                version={view.version}
                onClose={() => setView({ kind: 'list' })}
            />
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {newDxDraftPrompt && (
                <NewDxReportDraftModal
                    existingDraft={newDxDraftPrompt}
                    onResume={handleResumeNewDraft}
                    onStartFresh={handleStartFresh}
                    onCancel={() => setNewDxDraftPrompt(null)}
                />
            )}

            {newVersionPrompt && (
                <NewVersionFromPreviousModal
                    previousEvent={newVersionPrompt}
                    onStartOver={handleStartOverFromPrevious}
                    onFromPrevious={handleFromPrevious}
                    onCancel={() => setNewVersionPrompt(null)}
                />
            )}

            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                {mode == null ? (
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                    >
                        <ArrowLeftIcon className="w-3.5 h-3.5" />
                        Back to actions
                    </button>
                ) : (
                    <div className="w-[100px]" /> /* spacer to keep title centered when hosted in a workflow */
                )}
                <div className="text-sm font-semibold text-gray-800">Dx report</div>
                <button
                    onClick={handleNewDxReport}
                    className="flex items-center gap-1.5 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                    <PlusIcon className="w-3.5 h-3.5" />
                    {sortedEvents.some((e) => e.status === 'active')
                        ? 'Create new version'
                        : 'New Dx report'}
                </button>
            </div>

            {previewError && (
                <div className="mx-5 mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <span>{previewError}</span>
                    <button
                        onClick={() => setPreviewError(null)}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Dismiss preview error"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            )}

            {draftActionError && (
                <div className="mx-5 mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <span>{draftActionError}</span>
                    <button
                        onClick={() => setDraftActionError(null)}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Dismiss draft error"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-auto">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                    </div>
                )}

                {error && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-xs text-red-500">{error}</div>
                    </div>
                )}

                {!loading && !error && sortedEvents.length === 0 && drafts.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
                            <DocumentTextIcon className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="text-center">
                            <div className="text-sm font-semibold text-gray-800">
                                No Dx reports yet
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                Create the first diagnostic report for this patient.
                            </div>
                        </div>
                        <button
                            onClick={handleNewDxReport}
                            className="flex items-center gap-1.5 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" />
                            New Dx report
                        </button>
                    </div>
                )}

                {!loading && !error && (drafts.length > 0 || sortedEvents.length > 0) && (
                    <div className="p-5 flex flex-col gap-2">
                        {drafts.length > 0 && (
                            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                Drafts
                            </div>
                        )}
                        {drafts.map((draft) => {
                            const publishedParent = events.find((e) => e.groupId === draft.groupId);
                            const isDeletingThis = deletingDraftId === draft.id;
                            const lastEditedAt = draft.updatedAt || draft.createdAt;
                            const reviewInfo = getDraftReviewInfo(draft);
                            return (
                                <div
                                    key={draft.id}
                                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-yellow-200 bg-yellow-50/40 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                            <DocumentTextIcon className="w-4 h-4 text-yellow-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-800 truncate">
                                                    {publishedParent
                                                        ? buildFileName(publishedParent)
                                                        : 'New Dx report draft'}
                                                </span>
                                                <span className="text-[10px] font-medium text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                                    v
                                                    {publishedParent
                                                        ? publishedParent.version + 1
                                                        : 1}
                                                </span>
                                                <DraftStatusBadge reviewInfo={reviewInfo} />
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                Last edited by{' '}
                                                {draft.updatedByUser?.name ??
                                                    draft.createdByUser?.name ??
                                                    'someone'}{' '}
                                                · {formatRelativeTime(lastEditedAt)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() =>
                                                setView(
                                                    publishedParent
                                                        ? {
                                                              kind: 'edit',
                                                              event: publishedParent,
                                                              resumingDraft: draft
                                                          }
                                                        : { kind: 'create', resumingDraft: draft }
                                                )
                                            }
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            <PencilSquareIcon className="w-3.5 h-3.5" />
                                            Resume
                                        </button>
                                        <button
                                            onClick={() => setPendingDraftDelete(draft)}
                                            disabled={isDeletingThis}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {isDeletingThis ? (
                                                <div className="w-3.5 h-3.5 rounded-full border-2 border-red-400 border-t-transparent animate-spin" />
                                            ) : (
                                                <TrashIcon className="w-3.5 h-3.5" />
                                            )}
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {drafts.length > 0 && sortedEvents.length > 0 && (
                            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide pt-2">
                                Submitted
                            </div>
                        )}
                        {sortedEvents.map((event) => {
                            const hasPdf = !!getDxPatientFileId(event);
                            const isPreviewing = previewingId === event.id;
                            const fileName = buildFileName(event);
                            const date = new Date(event.createdAt);
                            const formattedDate = date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            });
                            const formattedTime = date.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit'
                            });

                            return (
                                <div
                                    key={event.id}
                                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                            <DocumentTextIcon className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-800 truncate">
                                                    {fileName}
                                                </span>
                                                <span className="text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                                    v{event.version}
                                                </span>
                                                {event.status === 'active' && event.isLatest && (
                                                    <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 rounded flex-shrink-0">
                                                        Active
                                                    </span>
                                                )}
                                                {event.status === 'invalidated' && (
                                                    <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                                        Deleted
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {formattedDate} at {formattedTime}
                                                {event.createdByUser && (
                                                    <> · By {event.createdByUser.name}</>
                                                )}
                                            </div>
                                            {event.status === 'invalidated' &&
                                                event.invalidationReason && (
                                                    <div className="text-[11px] text-gray-400 italic mt-0.5">
                                                        Deleted: {event.invalidationReason}
                                                    </div>
                                                )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handlePreview(event)}
                                            disabled={!hasPdf || isPreviewing}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {isPreviewing ? (
                                                <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                                            ) : (
                                                <EyeIcon className="w-3.5 h-3.5" />
                                            )}
                                            Preview
                                        </button>
                                        {isAdmin && event.status === 'active' && (
                                            <button
                                                onClick={() => setConfirmDelete(event)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                                            >
                                                <TrashIcon className="w-3.5 h-3.5" />
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {pendingDraftDelete !== null && (
                <DeleteDraftConfirmModal
                    onCancel={() => setPendingDraftDelete(null)}
                    onConfirm={handleConfirmDraftDelete}
                />
            )}

            {confirmDelete !== null && (
                <DeleteSubmittedConfirmModal
                    reason={deleteReason}
                    onReasonChange={setDeleteReason}
                    isDeleting={isDeleting}
                    error={deleteError}
                    onDismissError={() => setDeleteError(null)}
                    onCancel={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />
            )}
        </div>
    );
}

export { DxReportLandingPage };
